/**
 * Thumbnail slider for the product media gallery.
 *
 * Extends the theme's own <carousel-slider> so the horizontal behaviour
 * ("thumbnails below") is inherited unchanged, and adds on top of it:
 *   - a vertical axis for the "thumbnails left" / "thumbnails right" positions
 *   - a visible-thumbnail count that caps the height of the vertical strip
 *   - keeping the active thumbnail in view whenever the main gallery changes
 *
 * The axis is read back from the rendered layout (the grid's flex-direction)
 * rather than from a data attribute, so it follows the CSS breakpoints and
 * stays correct when the viewport is resized.
 */
(() => {
  const CarouselSlider = customElements.get('carousel-slider');
  if (!CarouselSlider || customElements.get('thumbnail-slider')) return;

  class ThumbnailSlider extends CarouselSlider {
    init() {
      super.init();

      if (!this.galleryChangeHandler) {
        this.galleryChangeHandler = this.handleGalleryChange.bind(this);
        document.addEventListener('on:media-gallery:change', this.galleryChangeHandler);
      }

      this.revealActiveThumbnail('instant');
    }

    disconnectedCallback() {
      if (!this.galleryChangeHandler) return;
      document.removeEventListener('on:media-gallery:change', this.galleryChangeHandler);
      this.galleryChangeHandler = null;
    }

    /**
     * Handles the media gallery selecting a different item. Fired for every
     * gallery on the page, so it only acts on its own thumbnails.
     */
    handleGalleryChange() {
      const visibleCount = this.querySelectorAll('.slider__item:not([hidden])').length;

      // Media grouping hides and reorders thumbnails, so re-measure first.
      if (visibleCount !== this.slides.length) {
        this.refresh();
        return;
      }

      this.revealActiveThumbnail();
    }

    /**
     * Scrolls the active thumbnail into view.
     * @param {string} [transition] - Passed to the behavior parameter of scrollTo.
     */
    revealActiveThumbnail(transition) {
      if (!this.hasAttribute('loaded') || !this.offsetParent) return;

      const active = this.querySelector('.slider__item.is-active:not([hidden])');
      if (active) this.scrollToElement(active, transition);
    }

    /**
     * Caps the height of a vertical strip to the configured thumbnail count.
     */
    applyVisibleCount() {
      this.slider.style.maxHeight = '';

      const visible = parseInt(this.dataset.visibleCount, 10);
      if (!visible || this.slides.length <= visible) return;

      const last = this.slides[visible - 1];
      this.slider.style.maxHeight = `${last.offsetTop + last.offsetHeight - this.slides[0].offsetTop}px`;
    }

    initSlider() {
      this.isVertical = window.getComputedStyle(this.grid).flexDirection === 'column';

      if (!this.isVertical) {
        this.slider.style.maxHeight = '';
        super.initSlider();
        return;
      }

      this.applyVisibleCount();
      this.gridWidth = this.grid.clientWidth;

      this.slideSpan = this.slides[1].offsetTop - this.slides[0].offsetTop;
      if (!this.slideSpan) this.slideSpan = this.slides[0].offsetHeight;

      this.slidesToScroll = theme.settings.sliderItemsPerNav === 'page'
        ? Math.max(1, Math.floor(this.slider.clientHeight / this.slideSpan))
        : 1;

      this.currentIndex = Math.round(this.slider.scrollTop / this.slideSpan) || 0;

      this.setCarouselState(this.getMaxScroll() > 1);
      this.addListeners();

      if (this.nav) this.setButtonStates();
    }

    /**
     * @returns {number} - Scrollable distance remaining on the vertical axis.
     */
    getMaxScroll() {
      return this.slider.scrollHeight - this.slider.clientHeight;
    }

    addListeners() {
      if (!this.isVertical) {
        super.addListeners();
        return;
      }

      this.scrollHandler = debounce(this.handleScroll.bind(this), 100);
      this.slider.addEventListener('scroll', this.scrollHandler);

      if (this.nav) {
        this.navClickHandler = this.handleNavClick.bind(this);
        this.nav.addEventListener('click', this.navClickHandler);
      }
    }

    removeListeners() {
      if (!this.isVertical) {
        super.removeListeners();
        return;
      }

      this.slider.removeEventListener('scroll', this.scrollHandler);

      if (this.nav) {
        this.nav.removeEventListener('click', this.navClickHandler);
      }
    }

    handleScroll() {
      if (!this.isVertical) {
        super.handleScroll();
        return;
      }

      this.currentIndex = Math.round(this.slider.scrollTop / this.slideSpan);
      if (this.nav) this.setButtonStates();
    }

    handleNavClick(evt) {
      if (!this.isVertical) {
        super.handleNavClick(evt);
        return;
      }

      const btn = evt.target.closest('.slider-nav__btn');
      if (!btn) return;

      const distance = this.slidesToScroll * this.slideSpan;
      this.slider.scrollTo({
        top: this.slider.scrollTop + (btn.name === 'next' ? distance : -distance),
        behavior: 'smooth'
      });
    }

    handleResize() {
      if (!this.isVertical) {
        super.handleResize();
        return;
      }

      this.removeListeners();
      this.initSlider();
    }

    setButtonStates() {
      if (!this.isVertical) {
        super.setButtonStates();
        return;
      }

      this.prevBtn.disabled = this.slider.scrollTop <= 1;
      this.nextBtn.disabled = this.slider.scrollTop >= this.getMaxScroll() - 1;
    }

    scrollToElement(el, transition) {
      if (!this.isVertical) {
        super.scrollToElement(el, transition);
        return;
      }

      if (!el || this.slides.indexOf(el) === -1) return;

      const itemTop = el.offsetTop - this.slides[0].offsetTop;
      const itemBottom = itemTop + el.offsetHeight;
      const viewTop = this.slider.scrollTop;
      const viewBottom = viewTop + this.slider.clientHeight;

      if (itemTop >= viewTop && itemBottom <= viewBottom) return;

      this.slider.scrollTo({
        top: itemTop < viewTop ? itemTop : itemBottom - this.slider.clientHeight,
        behavior: transition || 'smooth'
      });
    }
  }

  customElements.define('thumbnail-slider', ThumbnailSlider);
})();
